import { computed } from "vue";
import _ from "lodash";
import { DraggerType, Facade } from "@/drawing";
import { toRulerSize, type RulerSize } from "@/drawing/overlay/rulerSizes";
import { trackTab } from "@/analytics/drawing-analytics";
import DraggerMenuState from "./DraggerMenuState";
import CanvasSettingsState from "./CanvasSettingsState";

/**
 * Mode Règle (outil Règle de la barre) : les règles affichées, et l'outil
 * Origine qui place leur point (0,0).
 * - Toucher l'outil affiche les règles et sélectionne l'Origine.
 * - Choisir un autre outil laisse les règles affichées (mode allumé).
 * - « Masquer les règles » (panneau de l'outil) éteint le mode et ne laisse
 *   aucun outil sélectionné.
 */

/** Épaisseur des règles, enregistrée avec les réglages du plan. */
export const rulerSize = computed<RulerSize>(() =>
  toRulerSize(DraggerMenuState.storedSettings?.dragger?.rulerSize),
);

/** L'outil Origine (celui de la Règle) est l'outil sélectionné. */
export const isRulerToolActive = computed(
  () => DraggerMenuState.activeDragger.name === DraggerType.Origin,
);

function persist(path: string, value: unknown) {
  DraggerMenuState.storedSettings = _.set(
    DraggerMenuState.storedSettings || {},
    path,
    value,
  );
  CanvasSettingsState.updateAndPersist();
}

export function setRulerVisible(visible: boolean) {
  if (visible === DraggerMenuState.rulerVisible) return;
  DraggerMenuState.rulerVisible = visible;
  trackTab("settings", "ruler_toggled", { value: visible });
  if (Facade.isInitialized) {
    if (visible) Facade.rulers.show();
    else Facade.rulers.hide();
  }
  persist("dragger.rulerVisible", visible);
}

/** Toucher l'outil Règle : règles affichées, Origine sélectionnée. */
export function selectRulerTool() {
  setRulerVisible(true);
  DraggerMenuState.setActiveDragger({ name: DraggerType.Origin });
}

/** « Masquer les règles » : le mode s'éteint, plus aucun outil sélectionné. */
export function hideRulers() {
  setRulerVisible(false);
  if (isRulerToolActive.value) {
    DraggerMenuState.setActiveDragger({ name: DraggerType.None });
  }
}

export function setRulerSize(size: RulerSize) {
  if (size === rulerSize.value) return;
  trackTab("settings", "ruler_size_changed", { value: size });
  persist("dragger.rulerSize", size);
  if (Facade.isInitialized) Facade.rulers.setSize(size);
}
